/**
 * sdConsoleLogs.ts
 * ─────────────────
 * Intercepts console.log / warn / error / info / debug
 * and keeps a rolling buffer of the last MAX_ENTRIES entries.
 *
 * Auto-initialises as soon as this module is first imported.
 * Safe to import multiple times — init runs only once.
 *
 * Security: NEVER stores raw input values.
 * Messages are stringified via safe serialisation that redacts
 * anything that looks like a password / token / secret key.
 */

const MAX_ENTRIES = 200;

export interface ConsoleEntry {
  ts: string;          // ISO timestamp
  level: "log" | "warn" | "error" | "info" | "debug";
  msg: string;         // Stringified, redacted message
}

// ── Internal state ────────────────────────────────────────────────────────────
const buffer: ConsoleEntry[] = [];
let initialised = false;

// ── Redaction ─────────────────────────────────────────────────────────────────
const SENSITIVE_KEYS_RE =
  /password|passwd|secret|token|auth|apikey|api_key|authorization|bearer|credential/i;

function safeStringify(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) {
    return `${value.name}: ${value.message}${value.stack ? "\n" + value.stack : ""}`;
  }
  try {
    return JSON.stringify(value, (key, val) => {
      if (SENSITIVE_KEYS_RE.test(key)) return "[REDACTED]";
      return val;
    });
  } catch {
    return String(value);
  }
}

function buildMessage(args: unknown[]): string {
  return args.map(safeStringify).join(" ").slice(0, 2000); // cap single line at 2 KB
}

function push(level: ConsoleEntry["level"], args: unknown[]) {
  const entry: ConsoleEntry = {
    ts: new Date().toISOString(),
    level,
    msg: buildMessage(args),
  };
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift(); // keep rolling window
}

// ── Public API ─────────────────────────────────────────────────────────────────

/** Returns a copy of the current log buffer (oldest → newest). */
export function getConsoleLogs(): ConsoleEntry[] {
  return [...buffer];
}

/** Returns the buffer formatted as a plain-text string. */
export function getConsoleLogsText(): string {
  if (buffer.length === 0) return "(no console output captured)";
  return buffer
    .map((e) => `[${e.ts}] [${e.level.toUpperCase().padEnd(5)}] ${e.msg}`)
    .join("\n");
}

/**
 * Returns a File object ready to append to FormData.
 * Filename: `console_logs_<timestamp>.txt`
 */
export function getConsoleLogsFile(): File {
  const text = getConsoleLogsText();
  const blob = new Blob([text], { type: "text/plain" });
  return new File([blob], `console_logs_${Date.now()}.txt`, { type: "text/plain" });
}

/** Clear the buffer (e.g. after a ticket is submitted). */
export function clearConsoleLogs(): void {
  buffer.length = 0;
}

// ── Interceptor ───────────────────────────────────────────────────────────────

export function initConsoleCapture(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;

  const levels = ["log", "warn", "error", "info", "debug"] as const;

  levels.forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push(level, args);
      original(...args);      // always forward to real console
    };
  });

  // Also capture unhandled errors & promise rejections
  window.addEventListener("error", (e) => {
    push("error", [
      `[Unhandled Error] ${e.message}`,
      e.error instanceof Error ? e.error.stack ?? "" : "",
    ]);
  });

  window.addEventListener("unhandledrejection", (e) => {
    push("error", [
      `[Unhandled Promise Rejection]`,
      e.reason instanceof Error
        ? `${e.reason.message}\n${e.reason.stack ?? ""}`
        : safeStringify(e.reason),
    ]);
  });
}
